import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { activeSettingsGroup, settingsGroups, visibleSettingsItems } from "./settings-nav";
import { meApi } from "../api/me";
import { usePlanScreens } from "../screens/usePlanScreens";
import { cn } from "@/lib/utils";

/**
 * Settings hub — a 3-column view: the app sidebar (left, from AppShell) lists
 * the settings categories; this hub's middle column lists the *active*
 * category's items; the right column renders the selected page (<Outlet/>).
 * Switching category happens in the app sidebar.
 */
export function SettingsLayout() {
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  const group = activeSettingsGroup(location.pathname) ?? settingsGroups[0];
  const GroupIcon = group.icon;

  const profile = useQuery({
    queryKey: ["me", "profile"],
    queryFn: () => meApi.get(),
    staleTime: 5 * 60_000,
  });

  const plan = usePlanScreens();

  // While the profile loads, hide. Showing "Ví & Thanh toán" and taking it away a moment later
  // reads as a glitch, and the endpoint refuses the click anyway — same rule the app sidebar uses.
  const items = visibleSettingsItems(group, {
    permissions: profile.data?.permissions ?? [],
    allowsPath: plan.allowsPath,
  });

  const nav = (
    <nav>
      <div className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80">
        {group.label}
      </div>
      <ul className="space-y-0.5">
        {items.map((item) => (
          <li key={item.path}>
            <NavLink
              to={item.path}
              onClick={() => setNavOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors",
                  isActive
                    ? "bg-muted font-medium text-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon size={16} className={cn("shrink-0", isActive && "text-primary")} />
                  <span className="truncate">{item.label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <GroupIcon size={18} />
        </div>
        <h1 className="text-lg font-bold text-foreground">Cài đặt · {group.label}</h1>
        {/* Mobile: small icon button to toggle the section list */}
        <button
          type="button"
          onClick={() => setNavOpen((v) => !v)}
          aria-label="Mục cài đặt"
          title="Mục cài đặt"
          className="ml-auto inline-flex h-9 w-9 items-center justify-center rounded-md border text-muted-foreground lg:hidden"
        >
          <Menu size={18} />
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
        <aside
          className={cn(
            "rounded-lg border bg-card p-2 lg:sticky lg:top-4 lg:block lg:self-start",
            navOpen ? "block" : "hidden"
          )}
        >
          {nav}
        </aside>
        <div className="min-w-0" key={location.pathname}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}
